import { Add, ArrowBack, Close, Search as SearchIcon } from "@mui/icons-material";
import { Box, Button, Checkbox, Divider, Menu, MenuItem, TextField } from "@mui/material";
import { useState } from "react";
import BlotterFilterPanel from "@/components/BlotterFilterPanel";
import { DATE_INPUT_LANG } from "@/constants/dates";
import { applyFilterDraft, draftFromFilters, filterChipText } from "@/utils/blotterFilter-helper";

export interface FilterOption {
  value: string;
  label: string;
}
export interface FilterField {
  id: string;
  label: string;
  options: FilterOption[];
  /**
   * A one-click boolean toggle rather than a multi-select list. Picking it from the
   * Add-filter menu toggles the filter on/off directly (value "true"); the chip shows
   * just the field label. `options` is ignored for boolean fields.
   */
  boolean?: boolean;
  /**
   * Renders a From/To date pair (with an Apply button) instead of an option list.
   * `options` is ignored for daterange fields; pass `options: []`. Applied `values` are
   * always `[from, to]`, each a `YYYY-MM-DD` string or "" for an open-ended side. Apply is
   * disabled while `from` is after `to`, so an inverted range is never emitted to `onFiltersChange`.
   */
  kind?: "daterange" | "text";
  /** Overrides the chip text for this field. Receives the applied `values`. */
  chipLabel?: (values: string[]) => string;
}
export type FilterOp = "is" | "isnot";
export interface AppliedFilter {
  fieldId: string;
  values: string[];
  /** Only set by the opt-in panel; absent means "is". */
  op?: FilterOp;
}
/** The panel's uncommitted selection for one field. */
export interface FilterDraft {
  op: FilterOp;
  values: string[];
}

/** Shown under the To input while Apply is disabled, so the dead button explains itself. */
const INVERTED_RANGE_MESSAGE = "From must be on or before To";

/**
 * True when a drafted daterange is inverted — `from` strictly after `to`, a range no row can ever
 * fall inside. An empty side is a legitimate open-ended range ("from Jun 1, no end" / "until
 * Jun 30, no start"), so both bounds must be present before a pair can be inverted.
 *
 * Compares the two `YYYY-MM-DD` strings lexicographically and never via `new Date()`: for that
 * fixed-width format, string order IS chronological order, and unlike a parsed Date it cannot be
 * shifted a day by the runtime's timezone (see `shortDate` in `utils/thankYouNotes-helper.ts` for
 * the boundary bug that rule exists to avoid).
 */
export function isInvertedDateRange(from: string, to: string): boolean {
  const start = from.trim();
  const end = to.trim();
  return start !== "" && end !== "" && start > end;
}

/** Pure: the applied filters after committing a text draft — removes the field when the trimmed draft is empty. */
export function applyTextFilter(
  filters: AppliedFilter[],
  fieldId: string,
  draft: string,
): AppliedFilter[] {
  const trimmed = draft.trim();
  if (!trimmed) return filters.filter((f) => f.fieldId !== fieldId);
  return filters.some((f) => f.fieldId === fieldId)
    ? filters.map((f) => (f.fieldId === fieldId ? { ...f, values: [trimmed] } : f))
    : [...filters, { fieldId, values: [trimmed] }];
}

interface BlotterFilterBarProps {
  placeholder: string;
  search: string;
  onSearchChange: (s: string) => void;
  fields: FilterField[];
  filters: AppliedFilter[];
  onFiltersChange: (f: AppliedFilter[]) => void;
  /** Opt-in Is / Is not + search + Cancel/Apply panel for multi-select fields. Default false. */
  panel?: boolean;
}

/**
 * BlotterFilterBar — the Blotter's search input plus applied-filter chips and an
 * "Add filter" popover, config-driven per surface. Emits `search` + `filters`; the
 * caller maps filters to API params. Used by the Users and Organizations blotters.
 */
export default function BlotterFilterBar({
  placeholder,
  search,
  onSearchChange,
  fields,
  filters,
  onFiltersChange,
  panel = false,
}: BlotterFilterBarProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [mode, setMode] = useState<string>("fields"); // "fields" or a fieldId
  // Local draft for the daterange submenu's two inputs, synced from the applied values
  // whenever that submenu opens (see `openMenu` and the "Add filter" field-list item below).
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  // Local draft for the text submenu's single input, synced from the applied values whenever
  // that submenu opens (see `openMenu` and the "Add filter" field-list item below).
  const [textDraft, setTextDraft] = useState("");
  // Bumped on every panel open so the panel remounts with a fresh draft from the applied filters.
  const [panelKey, setPanelKey] = useState(0);

  const fieldById = (id: string) => fields.find((f) => f.id === id);
  const valuesFor = (id: string) => filters.find((f) => f.fieldId === id)?.values ?? [];
  const optionLabel = (fieldId: string, value: string) =>
    fieldById(fieldId)?.options.find((o) => o.value === value)?.label ?? value;

  function syncRangeDraft(fieldId: string) {
    const values = valuesFor(fieldId);
    setRangeFrom(values[0] ?? "");
    setRangeTo(values[1] ?? "");
  }

  function syncTextDraft(fieldId: string) {
    setTextDraft(valuesFor(fieldId)[0] ?? "");
  }

  function openMenu(el: HTMLElement, m: string) {
    setAnchorEl(el);
    setMode(m);
    if (fieldById(m)?.kind === "daterange") {
      syncRangeDraft(m);
    }
    if (fieldById(m)?.kind === "text") {
      syncTextDraft(m);
    }
    if (panel) setPanelKey((k) => k + 1);
  }

  function applyPanel(fieldId: string, draft: FilterDraft) {
    onFiltersChange(applyFilterDraft(filters, fieldId, draft));
    setAnchorEl(null);
  }

  function applyDateRange(fieldId: string) {
    const from = rangeFrom.trim();
    const to = rangeTo.trim();
    // Apply is disabled while the range is inverted; this keeps that invariant true for every
    // caller instead of committing a filter that is guaranteed to match nothing.
    if (isInvertedDateRange(from, to)) return;
    const next =
      !from && !to
        ? filters.filter((f) => f.fieldId !== fieldId)
        : filters.some((f) => f.fieldId === fieldId)
          ? filters.map((f) => (f.fieldId === fieldId ? { ...f, values: [from, to] } : f))
          : [...filters, { fieldId, values: [from, to] }];
    onFiltersChange(next);
    setAnchorEl(null);
  }

  function applyText(fieldId: string) {
    onFiltersChange(applyTextFilter(filters, fieldId, textDraft));
    setAnchorEl(null);
  }

  function dateRangeChipText(field: FilterField, values: string[]) {
    if (field.chipLabel) return field.chipLabel(values);
    const [from = "", to = ""] = values;
    if (from && to) return `${field.label}: ${from} to ${to}`;
    if (from) return `${field.label}: from ${from}`;
    if (to) return `${field.label}: to ${to}`;
    return field.label;
  }

  function toggleValue(fieldId: string, value: string) {
    const existing = filters.find((f) => f.fieldId === fieldId);
    let next: AppliedFilter[];
    if (existing) {
      const values = existing.values.includes(value)
        ? existing.values.filter((v) => v !== value)
        : [...existing.values, value];
      next =
        values.length === 0
          ? filters.filter((f) => f.fieldId !== fieldId)
          : filters.map((f) => (f.fieldId === fieldId ? { ...f, values } : f));
    } else {
      next = [...filters, { fieldId, values: [value] }];
    }
    onFiltersChange(next);
  }

  function removeFilter(fieldId: string) {
    onFiltersChange(filters.filter((f) => f.fieldId !== fieldId));
  }

  const activeField = mode !== "fields" ? fieldById(mode) : null;
  // Only read inside the daterange submenu — every other field kind renders neither the From/To
  // inputs nor an Apply button, so this flag is inert for them.
  const isRangeInverted = isInvertedDateRange(rangeFrom, rangeTo);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flex: 1,
        flexWrap: "wrap",
        // Matches the design's `.sf-bar`: 10px radius, 7px/12px padding (no fixed height).
        border: "1px solid var(--color-smoke)",
        borderRadius: "10px",
        bgcolor: "background.paper",
        px: "12px",
        py: "7px",
        "&:focus-within": { borderColor: "var(--color-dark-dust)" },
      }}
    >
      <SearchIcon sx={{ fontSize: 20, color: "var(--color-graphite)" }} />

      {filters
        .filter((f) => f.values.length)
        .map((f) => {
          const field = fieldById(f.fieldId);
          return (
            <span
              key={f.fieldId}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "3px 4px 3px 10px",
                borderRadius: 9999,
                background: "var(--color-wax)",
                fontSize: 13,
                color: "var(--color-rodeo-black)",
                whiteSpace: "nowrap",
              }}
            >
              {field?.boolean ? (
                <strong style={{ fontWeight: 700 }}>{field?.label}</strong>
              ) : field?.kind === "daterange" ? (
                <button
                  type="button"
                  onClick={(e) => openMenu(e.currentTarget, f.fieldId)}
                  style={{
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    fontFamily: "var(--font-lato)",
                    fontSize: 13,
                    color: "inherit",
                    padding: 0,
                    fontWeight: 700,
                  }}
                >
                  {dateRangeChipText(field, f.values)}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => openMenu(e.currentTarget, f.fieldId)}
                  style={{
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    fontFamily: "var(--font-lato)",
                    fontSize: 13,
                    color: "inherit",
                    padding: 0,
                  }}
                >
                  {panel && field ? (
                    filterChipText(field, f, true)
                  ) : (
                    <>
                      <strong style={{ fontWeight: 700 }}>{field?.label}:</strong>{" "}
                      {f.values.map((v) => optionLabel(f.fieldId, v)).join(", ")}
                    </>
                  )}
                </button>
              )}
              <Close
                onClick={() => removeFilter(f.fieldId)}
                sx={{ fontSize: 15, cursor: "pointer", color: "var(--color-graphite)" }}
              />
            </span>
          );
        })}

      <input
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        style={{
          border: "none",
          outline: "none",
          background: "transparent",
          flex: 1,
          minWidth: 120,
          fontFamily: "var(--font-lato)",
          fontSize: 14,
          color: "var(--color-rodeo-black)",
        }}
      />

      {/* Hidden when the blotter defines no filter fields (e.g. server-side blotters that filter via
          their own selectors) — otherwise it'd be a dead control. */}
      {fields.length > 0 && (
        <button
          type="button"
          onClick={(e) => openMenu(e.currentTarget, "fields")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            // Matches the design's `.sf-add`: dashed rodeo-dust border, rodeo-black label.
            padding: "5px 12px 5px 9px",
            border: "1px dashed var(--color-rodeo-dust)",
            borderRadius: 9999,
            background: "none",
            cursor: "pointer",
            color: "var(--color-rodeo-black)",
            fontFamily: "var(--font-lato)",
            fontSize: 13,
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          <Add sx={{ fontSize: 16 }} /> Add filter
        </button>
      )}

      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {mode === "fields"
          ? fields.map((f) =>
              f.boolean ? (
                <MenuItem
                  key={f.id}
                  onClick={() => toggleValue(f.id, "true")}
                  sx={{ fontSize: 14, minWidth: 180 }}
                >
                  <Checkbox
                    checked={valuesFor(f.id).length > 0}
                    size="small"
                    sx={{ p: 0.5, mr: 1 }}
                  />
                  {f.label}
                </MenuItem>
              ) : (
                <MenuItem
                  key={f.id}
                  onClick={() => {
                    setMode(f.id);
                    if (f.kind === "daterange") syncRangeDraft(f.id);
                    if (f.kind === "text") syncTextDraft(f.id);
                    if (panel) setPanelKey((k) => k + 1);
                  }}
                  sx={{ fontSize: 14, minWidth: 180 }}
                >
                  {f.label}
                  {f.kind !== "daterange" && f.kind !== "text" && valuesFor(f.id).length > 0
                    ? ` (${valuesFor(f.id).length})`
                    : ""}
                </MenuItem>
              ),
            )
          : activeField
            ? [
                <MenuItem
                  key="__back"
                  onClick={() => setMode("fields")}
                  sx={{ fontSize: 13, fontWeight: 700, color: "var(--color-graphite)" }}
                >
                  <ArrowBack sx={{ fontSize: 16, mr: 1 }} />
                  {activeField.label}
                </MenuItem>,
                <Divider key="__div" />,
                ...(activeField.kind === "daterange"
                  ? [
                      <Box
                        key="__daterange"
                        // MUI's MenuList listens for keydown on the <ul role="menu"> above and
                        // calls preventDefault() unconditionally for ArrowDown/ArrowUp/Home/End,
                        // swallowing the arrows the native date inputs use to step their
                        // day/month/year segments. Escape is let through so the Menu still closes.
                        onKeyDown={(e) => {
                          if (e.key !== "Escape") e.stopPropagation();
                        }}
                        sx={{
                          px: 2,
                          py: 1.5,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                          minWidth: 220,
                        }}
                      >
                        <TextField
                          type="date"
                          lang={DATE_INPUT_LANG}
                          size="small"
                          label="From"
                          value={rangeFrom}
                          onChange={(e) => setRangeFrom(e.target.value)}
                          error={isRangeInverted}
                          slotProps={{ inputLabel: { shrink: true } }}
                          fullWidth
                        />
                        <TextField
                          type="date"
                          lang={DATE_INPUT_LANG}
                          size="small"
                          label="To"
                          value={rangeTo}
                          onChange={(e) => setRangeTo(e.target.value)}
                          error={isRangeInverted}
                          // MUI wires helperText to the input via aria-describedby, so the reason
                          // Apply is disabled is announced, not just drawn.
                          helperText={isRangeInverted ? INVERTED_RANGE_MESSAGE : undefined}
                          slotProps={{ inputLabel: { shrink: true } }}
                          fullWidth
                        />
                        <Button
                          variant="contained"
                          size="small"
                          disabled={isRangeInverted}
                          onClick={() => applyDateRange(activeField.id)}
                        >
                          Apply
                        </Button>
                      </Box>,
                    ]
                  : activeField.kind === "text"
                    ? [
                        <Box
                          key="__text"
                          // Same reason as the daterange submenu above: MUI's MenuList listens for
                          // keydown on the <ul role="menu"> and would swallow keystrokes typed into
                          // this input via its typeahead/navigation handling. Escape is let through
                          // so the Menu still closes.
                          onKeyDown={(e) => {
                            if (e.key !== "Escape") e.stopPropagation();
                          }}
                          sx={{
                            px: 2,
                            py: 1.5,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                            minWidth: 220,
                          }}
                        >
                          <TextField
                            size="small"
                            label={activeField.label}
                            value={textDraft}
                            onChange={(e) => setTextDraft(e.target.value)}
                            onKeyDown={(e) => {
                              // Without preventDefault, Enter survives the menu closing (applyText
                              // sets anchorEl null) and re-activates the "Add filter" button, which
                              // just regained focus — reopening the menu it was meant to dismiss.
                              if (e.key === "Enter") {
                                e.preventDefault();
                                applyText(activeField.id);
                              }
                            }}
                            slotProps={{ inputLabel: { shrink: true } }}
                            fullWidth
                            autoFocus
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => applyText(activeField.id)}
                          >
                            Apply
                          </Button>
                        </Box>,
                      ]
                    : panel
                      ? [
                          <BlotterFilterPanel
                            key={`__panel-${activeField.id}-${panelKey}`}
                            field={activeField}
                            initialDraft={draftFromFilters(filters, activeField.id)}
                            onApply={(draft) => applyPanel(activeField.id, draft)}
                            onCancel={() => setAnchorEl(null)}
                          />,
                        ]
                      : activeField.options.map((o) => (
                          <MenuItem
                            key={o.value}
                            onClick={() => toggleValue(activeField.id, o.value)}
                            sx={{ fontSize: 14, minWidth: 200 }}
                          >
                            <Checkbox
                              checked={valuesFor(activeField.id).includes(o.value)}
                              size="small"
                              sx={{ p: 0.5, mr: 1 }}
                            />
                            {o.label}
                          </MenuItem>
                        ))),
              ]
            : null}
      </Menu>
    </Box>
  );
}
